import { Resource } from "@common/utils/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const PROTO_KEYS = new Set(["__proto__", "constructor", "prototype"]);
function safeParse(raw: string): unknown {
    return JSON.parse(raw, (key, val) => (PROTO_KEYS.has(key) ? undefined : val));
}

function isResource(val: unknown): val is Resource {
    return (
        typeof val === "object" &&
        val !== null &&
        typeof (val as Resource).resource_name === "string" &&
        typeof (val as Resource).sort_index === "number"
    );
}

function isResourceArray(val: unknown): val is Resource[] {
    return Array.isArray(val) && val.every(isResource);
}

type StoredSingle = { accountId: string; data: Resource };
type StoredMulti = { accountId: string; data: Resource[] };

/**
 * Get the selected resource from local storage.
 * Returns null for legacy plain-object format (no accountId) to force re-validation.
 */
const getLocalResource = (): StoredSingle | null => {
    try {
        const raw = localStorage.getItem("selectedResource");
        if (!raw) return null;
        const parsed: unknown = safeParse(raw);
        // Legacy format — plain Resource object without accountId wrapper
        if (parsed !== null && typeof parsed === "object" && !("accountId" in parsed)) return null;
        const typed = parsed as StoredSingle;
        if (typeof typed.accountId !== "string" || !isResource(typed.data)) {
            localStorage.removeItem("selectedResource");
            return null;
        }
        return typed;
    } catch {
        localStorage.removeItem("selectedResource");
        return null;
    }
};

/**
 * Get the selected resources from local storage.
 * Returns null for legacy plain-array format (no accountId) to force re-validation.
 */
const getLocalResources = (): StoredMulti | null => {
    try {
        const raw = localStorage.getItem("selectedResources");
        if (!raw) return null;
        const parsed: unknown = safeParse(raw);
        // Legacy format — plain array without accountId wrapper
        if (Array.isArray(parsed)) return null;
        if (parsed !== null && typeof parsed === "object" && !("accountId" in parsed)) return null;
        const typed = parsed as StoredMulti;
        if (typeof typed.accountId !== "string" || !isResourceArray(typed.data)) {
            localStorage.removeItem("selectedResources");
            return null;
        }
        return typed;
    } catch {
        localStorage.removeItem("selectedResources");
        return null;
    }
};

// Deferred hydration: do not load from localStorage at module init time.
// At init we don't know the current accountId, so loading here would risk
// serving stale data from a different clinic after a hard refresh.
// Dispatch hydrateFromStorage once accountId is known (e.g. in AccountProvider).
const initialState: {
    selectedResource: Resource | null;
    selectedResources: Resource[];
} = {
    selectedResource: null,
    selectedResources: [],
};

const resourcesSlice = createSlice({
    name: "resources",
    initialState,
    reducers: {
        /**
         * Hydrate Redux state from localStorage once the current accountId is known.
         * Validates stored accountId matches — mismatched (stale) data is discarded.
         */
        hydrateFromStorage(state, action: PayloadAction<{ accountId: string }>) {
            const { accountId } = action.payload;
            // Always reset before loading — prevents stale account-A data persisting if
            // hydrateFromStorage fires before clearResourceState during impersonation switches.
            state.selectedResources = [];
            state.selectedResource = null;

            const storedMulti = getLocalResources();
            if (storedMulti && storedMulti.accountId === accountId) {
                state.selectedResources = storedMulti.data;
            }
            const storedSingle = getLocalResource();
            if (storedSingle && storedSingle.accountId === accountId) {
                state.selectedResource = storedSingle.data;
                // Belt-and-suspenders: if the resource carries its own account_id field, verify it matches.
                // Guards against edge cases where the wrapper accountId matches but the payload was written for a different account.
                if (state.selectedResource?.account_id && state.selectedResource.account_id !== accountId) {
                    state.selectedResource = null;
                }
            }
        },
        resourceSelected(state, action: PayloadAction<{ resource: Resource; accountId: string }>) {
            state.selectedResource = action.payload.resource;
        },
        resourcesSelected(state, action: PayloadAction<{ resources: Resource[]; accountId: string }>) {
            state.selectedResources = action.payload.resources;
            // For backward compatibility, set the first resource as selectedResource
            state.selectedResource = action.payload.resources.length > 0 ? action.payload.resources[0] : null;
        },
        clearResourceState(state) {
            state.selectedResource = null;
            state.selectedResources = [];
        },
    },
});

export const { hydrateFromStorage, resourceSelected, resourcesSelected, clearResourceState } = resourcesSlice.actions;

export default resourcesSlice.reducer;
